/**
 * PageHeader (HD-PASS 2, 2026-09-01) — ONE title language for every page.
 *
 * Before this, page titles ran 20px → 36px across the app (Invoices xl, PO 2xl,
 * Team 3xl, Settings 4xl) and the top bar repeated the name a second time.
 * Now: the page OWNS its title at one size — the 4xl extrabold the Today /
 * Settings / Memberships HD passes already established — and the top bar
 * shows a breadcrumb instead (Header.tsx), the Linear / Notion split.
 *
 *   <PageHeader icon={ShoppingCart} title="Purchase Orders"
 *     description="Pegasus POs you create, plus the full ESC purchase history."
 *     actions={<Button>New PO</Button>} />
 *
 * `kicker` = an optional small-caps line above the title (Today uses one for
 * "TODAY · COMMAND CENTER"); `children` renders under the description for
 * page-level chips / stats / tab strips.
 */
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

export function PageHeader({
  icon: Icon,
  title,
  description: _description,
  actions,
  kicker,
  className,
  children,
}: {
  icon?: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  title: ReactNode;
  description?: ReactNode;
  /** Right-aligned buttons / controls. */
  actions?: ReactNode;
  kicker?: ReactNode;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <header className={cn("mb-6 flex flex-wrap items-end justify-between gap-x-6 gap-y-3", className)}>
      <div className="min-w-0 flex-1">
        {kicker && (
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">{kicker}</p>
        )}
        <h2 className="flex items-center gap-3 text-4xl font-extrabold leading-none tracking-tight text-slate-900">
          {Icon && (
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-100">
              <Icon className="h-5 w-5" strokeWidth={2} />
            </span>
          )}
          <span className="min-w-0">{title}</span>
        </h2>
        {/* DESIGN-CALM-01 (Seth 2026-09-18): no sentences in the chrome. The
            description prop stays for the callers, but a page title explains
            itself — the line under it is gone everywhere at once. */}
        {children}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

/**
 * SectionTitle — the in-page heading (a card, a table, a group of rows). The
 * Today v4 / 360-HD language: a real bold title, an optional h-8 icon chip,
 * a quiet count pill, a hint, and right-side actions. Replaces the 11px
 * whisper-caps `h3`s that made sections read as fine print.
 *
 *   <SectionTitle icon={Package} title="Pull Sheet" count={list.length}
 *     actions={<CopyListButton … />} />
 */
export function SectionTitle({
  icon: Icon,
  title,
  count,
  hint,
  actions,
  size = "md",
  tone = "slate",
  className,
  as: Tag = "h3",
}: {
  icon?: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  title: ReactNode;
  count?: number | null;
  hint?: ReactNode;
  actions?: ReactNode;
  /** md = 15px (cards, groups) · lg = 17px (page sections, Today v4). */
  size?: "md" | "lg";
  tone?: "slate" | "brand" | "amber" | "emerald" | "red";
  className?: string;
  as?: "h2" | "h3" | "h4" | "div";
}) {
  const chipTone = {
    slate: "bg-slate-100 text-slate-600",
    brand: "bg-brand-50 text-brand-700",
    amber: "bg-amber-50 text-amber-700",
    emerald: "bg-emerald-50 text-emerald-700",
    red: "bg-red-50 text-red-700",
  }[tone];
  return (
    <div className={cn("flex flex-wrap items-center gap-x-2.5 gap-y-1", className)}>
      {Icon && (
        <span className={cn("flex shrink-0 items-center justify-center rounded-lg", size === "lg" ? "h-10 w-10 rounded-xl" : "h-8 w-8", chipTone)}>
          <Icon className={size === "lg" ? "h-5 w-5" : "h-4 w-4"} strokeWidth={1.9} />
        </span>
      )}
      <Tag className={cn("min-w-0 font-bold tracking-tight text-slate-900", size === "lg" ? "text-[17px]" : "text-[15px]")}>{title}</Tag>
      {count != null && (
        <span className="rounded-full bg-slate-100 px-1.5 py-px text-[11px] font-semibold tabular-nums text-slate-500">{count}</span>
      )}
      {hint && <span className="text-xs text-slate-400">{hint}</span>}
      {actions && <span className="ml-auto flex shrink-0 items-center gap-1.5">{actions}</span>}
    </div>
  );
}
